import { useCallback } from 'react';
import { DocumentType } from './document';
export const toIntList = (value) => {
    if (value == null) {
        return null;
    }
    if (Array.isArray(value)) {
        const nums = value.map(Number).filter((n) => !Number.isNaN(n));
        return nums.length > 0 ? nums : null;
    }
    const n = Number(value);
    return Number.isNaN(n) ? null : [n];
};
const toStringList = (value) => {
    if (value == null) {
        return null;
    }
    if (Array.isArray(value)) {
        const names = value.filter((v) => typeof v === 'string' && v.length > 0);
        return names.length > 0 ? names : null;
    }
    return typeof value === 'string' && value.length > 0 ? [value] : null;
};
const cellLocationFromUnknown = (cell) => {
    if (!cell) {
        return undefined;
    }
    const sheetName = cell.sheetName ?? cell.sheet_name;
    if (sheetName == null || typeof sheetName !== 'string') {
        return undefined;
    }
    return {
        sheetName,
        row: toIntList(cell.rowIndex ?? cell.row),
        col: toIntList(cell.colIndex ?? cell.col),
        columnName: toStringList(cell.columnName ?? cell.column_name),
    };
};
const extractCellFromLegacyCustomMessage = (messageContext) => {
    const customMessage = messageContext?.CUSTOM_MESSAGE;
    if (!customMessage) {
        return undefined;
    }
    let metadata;
    try {
        metadata =
            typeof customMessage === 'string'
                ? JSON.parse(customMessage)
                : customMessage;
    }
    catch {
        return undefined;
    }
    const cell = (metadata.excelMetadata ?? metadata.excel_metadata ?? metadata);
    return cellLocationFromUnknown(cell);
};
const extractFindingCellCoordinates = (finding) => {
    const excelMetadata = finding.excelMetadata;
    const primary = cellLocationFromUnknown(excelMetadata?.primary ?? undefined) ??
        extractCellFromLegacyCustomMessage(finding.messageContext);
    const ref = cellLocationFromUnknown(excelMetadata?.ref ?? undefined);
    return { primary, ref };
};
export const useConvertFindingToDocument = (t) => {
    return useCallback((finding) => {
        const findingCoordinates = finding.x0 != null &&
            finding.x1 != null &&
            finding.y0 != null &&
            finding.y1 != null &&
            finding.pageNumber != null
            ? {
                x0: finding.x0,
                x1: finding.x1,
                y0: finding.y0,
                y1: finding.y1,
                pageIndex: finding.pageNumber,
            }
            : undefined;
        const refFindingCoordinates = finding.refX0 != null &&
            finding.refX1 != null &&
            finding.refY0 != null &&
            finding.refY1 != null &&
            finding.refPageNumber != null
            ? {
                x0: finding.refX0,
                x1: finding.refX1,
                y0: finding.refY0,
                y1: finding.refY1,
                pageIndex: finding.refPageNumber,
            }
            : undefined;
        const { primary: findingCellCoordinates, ref: refFindingCellCoordinates } = extractFindingCellCoordinates(finding);
        return {
            id: finding.blobId,
            name: finding.fileName ?? t('findings.project-related-finding'),
            documentPackageId: finding.documentPackageId,
            extension: finding.fileName
                ? '.' + finding.fileName.toLocaleLowerCase().split('.').pop()
                : '',
            pageNumber: finding.pageNumber,
            content: finding.content ?? t('findings.no-document-reference'),
            type: DocumentType.FINDING,
            refBlobId: finding.refBlobId,
            refDocumentPackageId: finding.refDocumentPackageId,
            refContent: finding.refContent,
            refFileName: finding.refFileName,
            refPageNumber: finding.refPageNumber,
            conflictType: finding.conflictType,
            conflictCategory: finding.conflictCategory,
            messageTemplate: finding.messageTemplate,
            messageContext: finding.messageContext,
            messageContextEn: finding.messageContextEn,
            findingId: finding.id,
            projectId: finding.projectId,
            findingCoordinates: findingCoordinates,
            refFindingCoordinates: refFindingCoordinates,
            findingCellCoordinates: findingCellCoordinates,
            refFindingCellCoordinates: refFindingCellCoordinates,
            comments: finding.comments,
            fileUrl: finding.fileUrl,
            downloadUrl: finding.downloadUrl,
        };
    }, [t]);
};
export const convertBlobToDocument = (blob) => {
    return {
        id: blob.id,
        name: blob.name,
        nameSuggestion: blob.nameSuggestion || undefined,
        documentPackageId: blob.documentPackage,
        extension: blob.extension,
        parsedData: blob.parsedData,
        type: DocumentType.BLOB,
        ns3451: blob.ns3451,
        convertedPdfFileId: blob.convertedPdfFileId,
        fileUrl: blob.fileUrl,
        downloadUrl: blob.downloadUrl,
        scheduleCheckFileType: blob.scheduleCheckFileType,
    };
};
export const convertFileToDocument = (file) => {
    return {
        id: file.id,
        name: file.originalFilename,
        extension: file.filenameExtension,
        projectId: file.projectId,
        type: DocumentType.FILE,
        fileUrl: file.fileUrl,
        downloadUrl: file.fileUrl,
    };
};
export const convertFileToDocumentWithFileType = (file, type) => {
    const document = convertFileToDocument(file);
    return {
        ...document,
        fileType: type,
    };
};
export const convertVersionComparisonToDocument = (comparison, side) => {
    const isVersionA = side === 'a';
    const blobId = isVersionA ? comparison.blobId : comparison.refBlobId;
    const blobName = isVersionA ? comparison.blobName : comparison.refBlobName;
    const pageNumber = isVersionA ? comparison.pageNumber : comparison.refPageNumber;
    const x0 = isVersionA ? comparison.x0 : comparison.refX0;
    const y0 = isVersionA ? comparison.y0 : comparison.refY0;
    const x1 = isVersionA ? comparison.x1 : comparison.refX1;
    const y1 = isVersionA ? comparison.y1 : comparison.refY1;
    const fileUrl = isVersionA ? comparison.fileUrl : comparison.refFileUrl;
    const findingCoordinates = x0 != null && x1 != null && y0 != null && y1 != null && pageNumber != null
        ? { x0, x1, y0, y1, pageIndex: pageNumber }
        : undefined;
    const extension = blobName ? '.' + blobName.toLowerCase().split('.').pop() : '';
    return {
        id: blobId ?? comparison.id.toString(),
        name: blobName ?? '',
        documentPackageId: comparison.documentPackageId,
        extension,
        pageNumber: pageNumber ?? 1,
        type: DocumentType.BLOB,
        ns3451: [],
        parsedData: {},
        convertedPdfFileId: null,
        fileUrl: fileUrl ?? '',
        downloadUrl: fileUrl ?? '',
        findingCoordinates,
    };
};
